import { ErrorMessage, Field, FieldProps, useFormikContext } from 'formik';
import { FC, useEffect, useState } from 'react';
import { Button, Form, FormControl, InputGroup } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { FormLabel } from '../../forms';

export interface FormSelectOptions {
  value: string | number | boolean;
  label: string;
}

const hasFormSelectValue = (value: unknown, multiple?: boolean): boolean => {
  if (multiple) {
    return Array.isArray(value) && value.length > 0;
  }
  if (value == null) {
    return false;
  }
  if (typeof value === 'object') {
    const option = value as FormSelectOptions;
    return (
      option.value !== undefined &&
      option.value !== null &&
      option.value !== '' &&
      option.value !== 0
    );
  }
  return true;
};

const toSelectedOptions = (selected: unknown[]): FormSelectOptions[] =>
  selected.filter(
    (item): item is FormSelectOptions =>
      !!item && typeof item === 'object' && 'value' in item && 'label' in item
  );

interface FormSelectProps {
  name: string;
  label: string;
  options: FormSelectOptions[];
  multiple?: boolean;
  loadingError?: string;
  onClick?: (values: FormSelectOptions[]) => void;
  onSelectionChange?: (values: FormSelectOptions[]) => void;
  selected_Value?: FormSelectOptions;
  className?: string;
  align?: 'justify' | 'left' | 'right';
  flip?: boolean;
  positionFixed?: boolean;
  dropup?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export const FormSelect: FC<FormSelectProps> = ({
  name,
  label,
  options,
  multiple,
  loadingError,
  onClick,
  onSelectionChange,
  selected_Value,
  className,
  align = 'justify',
  flip = true,
  positionFixed = true,
  dropup,
  disabled,
  required
}) => {
  const { setFieldValue, values } = useFormikContext<any>();
  const [initialized, setInitialized] = useState(false);

  // Initialize value from selected_Value only once when component mounts
  useEffect(() => {
    if (!initialized && selected_Value) {
      setFieldValue(name, multiple ? [selected_Value] : selected_Value);
      setInitialized(true);
    }
  }, [selected_Value, name, setFieldValue, multiple, initialized]);

  return (
    <Form.Group controlId={name}>
      <FormLabel required={required}>{label}</FormLabel>
      <InputGroup>
        <Field name={name}>
          {({ field, form }: FieldProps) => {
            const currentValue = field.value;
            const selected = multiple
              ? currentValue || []
              : hasFormSelectValue(currentValue, multiple)
                ? [currentValue]
                : [];

            const error = form.errors[name];
            const isInvalid = !!error;

            return (
              <>
                <Typeahead
                  id={name}
                  labelKey="label"
                  options={options}
                  multiple={multiple}
                  placeholder="Any"
                  selected={selected}
                  disabled={disabled}
                  flip={flip}
                  positionFixed={positionFixed}
                  align={align}
                  dropup={dropup}
                  inputProps={{ 'aria-required': required || undefined }}
                  onChange={(selected) => {
                    const nextValue = multiple ? selected : selected[0] || null;
                    form.setFieldValue(name, nextValue);
                    onSelectionChange?.(toSelectedOptions(selected));
                  }}
                  onInputChange={(text, e) => {
                    // Clear the field when user backspaces all text
                    if (text === '') {
                      form.setFieldValue(name, multiple ? [] : null);
                      onSelectionChange?.([]);
                    }
                  }}
                  className={['sfc-typeahead', className, isInvalid ? 'is-invalid' : '']
                    .filter(Boolean)
                    .join(' ')}
                  allowNew={false}
                  clearButton
                />
                <ErrorMessage name={name}>
                  {(msg: any) => (
                    <FormControl.Feedback type="invalid">
                      {typeof msg === 'object' ? (msg?.value ?? 'Invalid') : msg}
                    </FormControl.Feedback>
                  )}
                </ErrorMessage>
                {loadingError && <Form.Text className="text-danger">{loadingError}</Form.Text>}
                {onClick && (
                  <Button
                    type="button"
                    onClick={() => onClick(toSelectedOptions(selected))}
                  >
                    Apply
                  </Button>
                )}
              </>
            );
          }}
        </Field>
      </InputGroup>
    </Form.Group>
  );
};

export default FormSelect;
